import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useActions, useMe, useOneOnOneStats, useOrgMe, useSignalDefinitions, useSignals, useSummary, useTransitionAction, type Action, type Signal } from "../api/hooks";
import { SummaryCard } from "../components/summary-card";
import { Page, Chip, Table, statusTone, Loading, ErrorBox, Empty, Button } from "../components/ui";
import { SignalCard } from "../components/signal-card";
import { Bar, LineChart, Ring } from "../components/charts";
import { deltaText, latestByScope, toneFor, unitOf } from "../lib/signals";
import { dimensions, dimensionScore, healthSeries, healthTone, howItsMade } from "../lib/health";
import { fmtDate, plural, relative, title } from "../lib/format";
import { audienceLabel, usePersonNames } from "../lib/names";

export const Route = createFileRoute("/")({ component: Dashboard });

const OPEN = "recommended,awaiting_approval,assigned,accepted,in_progress";
/** Open actions ranked: the ones nobody has picked up yet first, then the oldest. */
const rank: Record<string, number> = { recommended: 0, awaiting_approval: 1, assigned: 2, accepted: 3, in_progress: 4 };

function fmt(v: number, unit: string) {
  const n = v.toLocaleString(undefined, { maximumFractionDigits: unit === "pct" ? 0 : 1 });
  return unit === "pct" ? `${n}%` : unit ? `${n} ${unit}` : n;
}

function Dashboard() {
  const me = useMe();
  const isAdmin = me.data?.roles.some((r) => r === "hr_admin" || r === "platform_admin") ?? false;
  if (me.data && !isAdmin) return <Navigate to="/team" replace />;
  return <AdminDashboard />;
}

function AdminDashboard() {
  const me = useMe();
  const org = useOrgMe();
  const defs = useSignalDefinitions();
  const signals = useSignals({ scope_type: "org", scope_key: "all" });
  const depts = useSignals({ scope_type: "dept" });
  const nameOf = usePersonNames();
  const open = useActions({ state: OPEN });
  const recommended = useActions({ state: "recommended" });
  const ones = useOneOnOneStats();
  const summary = useSummary("org", "all");
  const transition = useTransitionAction();
  const [error, setError] = useState<unknown>(null);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const name = org.data?.me?.full_name?.split(" ")[0] ?? me.data?.email?.split("@")[0] ?? "";

  const latest = useMemo(() => (signals.data ? latestByScope(signals.data) : new Map<string, { latest: Signal; previous?: Signal; series: Signal[] }>()), [signals.data]);
  const at = (k: string) => latest.get(`${k}|org|all`);
  const health = useMemo(() => healthSeries(signals.data ?? []), [signals.data]);
  const now = health[health.length - 1];
  const before = health[health.length - 2];
  const score = now?.score ?? null;
  const scoreDelta = score != null && before?.score != null ? score - before.score : null;

  const oneStats = ones.data ?? [];
  const completed = oneStats.reduce((a, s) => a + s.completed, 0);
  const due = oneStats.reduce((a, s) => a + s.completed + s.missed, 0);
  const onePct = due > 0 ? Math.round((100 * completed) / due) : null;

  const priorities = useMemo(() => [...(open.data ?? [])].sort((a, b) => (rank[a.state] ?? 9) - (rank[b.state] ?? 9) || (a.created_at < b.created_at ? -1 : 1)).slice(0, 5), [open.data]);

  // Largest week-on-week moves among the organisation's signals, worst first.
  const changes = useMemo(() => {
    const out: { key: string; latest: Signal; previous: Signal; pct: number; cls: "up" | "bad" | "" }[] = [];
    for (const [k, e] of latest) {
      if (!k.endsWith("|org|all") || !e.previous || e.previous.value === 0) continue;
      const pct = (e.latest.value - e.previous.value) / Math.abs(e.previous.value);
      if (Math.abs(pct) < 0.02) continue;
      out.push({ key: e.latest.signal_key, latest: e.latest, previous: e.previous, pct, cls: deltaText(e.latest.signal_key, e.latest, e.previous).cls });
    }
    return out.sort((a, b) => (a.cls === "bad" ? 0 : 1) - (b.cls === "bad" ? 0 : 1) || Math.abs(b.pct) - Math.abs(a.pct)).slice(0, 6);
  }, [latest]);

  // Each department's health this period and last, from its own aggregate signals.
  const segments = useMemo(() => {
    const byDept = new Map<string, Signal[]>();
    for (const s of depts.data ?? []) byDept.set(s.scope_key, [...(byDept.get(s.scope_key) ?? []), s]);
    return [...byDept.entries()]
      .map(([dept, list]) => {
        const h = healthSeries(list);
        const cur = h[h.length - 1], prev = h[h.length - 2];
        const l = latestByScope(list);
        return { dept, score: cur?.score ?? null, delta: cur?.score != null && prev?.score != null ? cur.score - prev.score : null, after: l.get(`after_hours_pct|dept|${dept}`)?.latest, load: l.get(`meeting_load_pct|dept|${dept}`)?.latest };
      })
      .sort((a, b) => (a.score ?? 101) - (b.score ?? 101));
  }, [depts.data]);

  const push = (a: Action) => transition.mutate({ id: a.id, state: "assigned" }, { onError: setError });

  return (
    <Page title={`${greeting}${name ? `, ${name}` : ""} 👋`} sub="Here's what's happening with your workforce this week.">
      {error != null && <div className="mb-3"><ErrorBox error={error} /></div>}
      {signals.isError && <ErrorBox error={signals.error} />}
      {signals.isPending ? (
        <Loading />
      ) : (
        <div className="grid grid-cols-5 gap-3">
          <SignalCard label="Burnout risk" unit={unitOf(defs.data, "after_hours_pct")} entry={at("after_hours_pct")} />
          <SignalCard label="Meeting load" unit={unitOf(defs.data, "meeting_load_pct")} entry={at("meeting_load_pct")} />
          <SignalCard label="Manager support" unit={unitOf(defs.data, "one_on_one_coverage_pct")} entry={at("one_on_one_coverage_pct")} />
          <div className="card kpi">
            <h4>1:1 completion</h4>
            <div className="v">
              <span>{onePct != null ? `${onePct}%` : "—"}</span>
              {onePct != null && <Chip tone={onePct >= 80 ? "good" : onePct >= 50 ? "elevated" : "high"}>{onePct >= 80 ? "On track" : onePct >= 50 ? "Slipping" : "Low"}</Chip>}
            </div>
            <div className="delta">{due > 0 ? `${completed} of ${due} scheduled 1:1s happened · ${plural(oneStats.length, "manager")}` : "No 1:1s scheduled yet"}</div>
          </div>
          <div className="card kpi" title={howItsMade}>
            <h4>Workforce health <span className="text-[10.5px] text-text-4 font-bold uppercase tracking-wide">first cut</span></h4>
            <div className="flex items-center gap-3">
              <Ring value={score} tone={healthTone(score)} />
              <div>
                <div className="text-[13px] font-bold">{score != null ? "/100" : "Not enough data"}</div>
                <div className={`delta !mt-1 ${scoreDelta == null ? "" : scoreDelta > 0 ? "up" : scoreDelta < 0 ? "bad" : ""}`}>
                  {scoreDelta == null ? "first period" : scoreDelta === 0 ? "no change vs last week" : `${scoreDelta > 0 ? "↑" : "↓"} ${Math.abs(scoreDelta)} pts vs last week`}
                </div>
              </div>
            </div>
            <div className="text-[11.5px] text-muted mt-2">Mean of {now ? Object.values(now.dims).filter((d) => d != null).length : 0} dimensions. Hover for how it's made.</div>
          </div>
        </div>
      )}

      <div className="mt-3"><SummaryCard summary={summary.data} pending={summary.isPending} /></div>

      <div className="grid gap-3 mt-3" style={{ gridTemplateColumns: "1.15fr 1.5fr 1fr" }}>
        <div className="card">
          <div className="flex items-baseline justify-between"><h4 className="font-bold text-[13.5px]">Top priorities</h4><span className="text-[11.5px] text-muted">by urgency and age</span></div>
          {open.isPending ? <Loading /> : priorities.length === 0 ? (
            <div className="text-[13px] text-muted mt-3">No open actions. They appear when a signal crosses a threshold or someone creates one.</div>
          ) : (
            <ol className="mt-3 flex flex-col gap-2.5">
              {priorities.map((a, i) => (
                <li key={a.id} className="flex gap-3 items-start text-[13px]">
                  <span className="text-[11px] font-bold text-text-4 w-4 pt-0.5">{i + 1}</span>
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold block truncate">{a.title}</span>
                    <span className="sub">{title(a.audience_type)} · {audienceLabel(a.audience_type, a.audience_key, nameOf)}{a.source_signal_key ? ` · ${title(a.source_signal_key).replace(" Pct", " %")}` : ""}</span>
                  </span>
                  <Chip tone={statusTone(a.state)}>{title(a.state)}</Chip>
                </li>
              ))}
            </ol>
          )}
          <div className="mt-3"><Link to="/actions" className="link">View all priorities →</Link></div>
        </div>

        <div className="card">
          <div className="flex items-baseline justify-between"><h4 className="font-bold text-[13.5px]">Workforce health over time</h4><span className="text-[11.5px] text-muted">weekly</span></div>
          <div className="mt-2">
            <LineChart points={health.map((p) => ({ label: fmtDate(p.period_start), value: p.score }))} tone={healthTone(score)} />
          </div>
          {scoreDelta != null && scoreDelta !== 0 && (
            <div className={`notice mt-2 text-[12.5px]`}>
              <b>{scoreDelta > 0 ? "↑" : "↓"} {Math.abs(scoreDelta)} points</b> {scoreDelta > 0 ? "improvement" : "decline"} on last week across {Object.values(now?.dims ?? {}).filter((d) => d != null).length} dimensions. <Link to="/workforce" className="link">Explore insights →</Link>
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex items-baseline justify-between"><h4 className="font-bold text-[13.5px]">Health by dimension</h4><span className="text-[11.5px] text-muted">vs last week</span></div>
          <div className="mt-3 flex flex-col gap-3">
            {dimensions.map((d) => {
              const e = at(d.signal);
              const s = dimensionScore(d, e?.latest.value);
              const p = dimensionScore(d, e?.previous?.value);
              const dd = s != null && p != null ? s - p : null;
              return (
                <div key={d.key} className="text-[12.5px]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold flex-1">{d.label}</span>
                    {dd != null && dd !== 0 && <span className={`delta !mt-0 ${dd > 0 ? "up" : "bad"}`}>{dd > 0 ? "↑" : "↓"} {Math.abs(dd)}</span>}
                    <span className="font-bold w-7 text-right">{s ?? "—"}</span>
                  </div>
                  <Bar value={s} tone={s == null ? "neutral" : healthTone(s)} />
                </div>
              );
            })}
          </div>
          <div className="mt-3"><Link to="/workforce" className="link">View full breakdown →</Link></div>
        </div>
      </div>

      <div className="grid gap-3 mt-3" style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1fr)" }}>
        <div className="card !p-0">
          <div className="flex items-baseline justify-between px-[18px] pt-4"><h4 className="font-bold text-[13.5px]">Insights by segment</h4><span className="text-[11.5px] text-muted">departments above the floor</span></div>
          {depts.isPending ? <div className="p-4"><Loading /></div> : segments.length === 0 ? (
            <div className="text-[13px] text-muted p-[18px]">No department is above the privacy floor yet, so nothing can be compared.</div>
          ) : (
            <Table
              rows={segments}
              keyOf={(r) => r.dept}
              cols={[
                { h: "Department", cell: (r) => <span className="font-semibold">{r.dept}</span> },
                { h: "Health", cell: (r) => <span className="font-bold">{r.score ?? "—"}</span>, w: "70px" },
                { h: "vs last", cell: (r) => r.delta == null || r.delta === 0 ? <span className="text-muted">—</span> : <span className={`delta !mt-0 ${r.delta > 0 ? "up" : "bad"}`}>{r.delta > 0 ? "↑" : "↓"} {Math.abs(r.delta)}</span>, w: "80px" },
                { h: "After-hours", cell: (r) => r.after ? <span className="flex items-center gap-1.5">{fmt(r.after.value, "pct")}<Chip tone={toneFor("after_hours_pct", r.after.value)}>{title(toneFor("after_hours_pct", r.after.value))}</Chip></span> : <span className="text-muted">—</span>, w: "150px" },
                { h: "Meeting load", cell: (r) => r.load ? <span className="flex items-center gap-1.5">{fmt(r.load.value, "pct")}<Chip tone={toneFor("meeting_load_pct", r.load.value)}>{title(toneFor("meeting_load_pct", r.load.value))}</Chip></span> : <span className="text-muted">—</span>, w: "150px" },
              ]}
            />
          )}
          <div className="px-[18px] pb-4 pt-2"><Link to="/workforce" className="link">View all departments →</Link></div>
        </div>

        <div className="card">
          <div className="flex items-baseline justify-between"><h4 className="font-bold text-[13.5px]">Recommended actions</h4><span className="text-[11.5px] text-muted">push to a manager</span></div>
          {recommended.isPending ? <Loading /> : (recommended.data?.length ?? 0) === 0 ? (
            <div className="text-[13px] text-muted mt-3">Nothing waiting. Recommendations arrive with the nightly signals.</div>
          ) : (
            <div className="mt-3 flex flex-col gap-2.5">
              {recommended.data!.slice(0, 4).map((a) => (
                <div key={a.id} className="flex gap-3 items-center text-[13px]">
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold block truncate">{a.title}</span>
                    <span className="sub truncate block">{a.rationale ?? `${title(a.audience_type)} · ${audienceLabel(a.audience_type, a.audience_key, nameOf)}`}</span>
                  </span>
                  {a.source_signal_key && <Chip tone={toneFor(a.source_signal_key, Number((a.evidence as { value?: number } | null)?.value ?? NaN))}>{title(a.sku)}</Chip>}
                  <Button variant="light" className="!py-1 !px-2.5 !text-[12px]" disabled={transition.isPending} onClick={() => push(a)}>Push</Button>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3"><Link to="/actions" className="link">View all actions →</Link></div>
        </div>

        <div className="card">
          <div className="flex items-baseline justify-between"><h4 className="font-bold text-[13.5px]">Recent changes</h4><span className="text-[11.5px] text-muted">week on week</span></div>
          {changes.length === 0 ? (
            <div className="text-[13px] text-muted mt-3">Nothing moved more than a couple of percent this week.</div>
          ) : (
            <div className="mt-3 flex flex-col gap-2.5">
              {changes.map((c) => (
                <div key={c.key} className="flex gap-2 items-start text-[12.5px]">
                  <span className={`font-bold w-4 ${c.cls === "bad" ? "text-[var(--status-high)]" : c.cls === "up" ? "text-[var(--status-good)]" : "text-muted"}`}>{c.pct > 0 ? "↑" : "↓"}</span>
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold block">{title(c.key).replace(" Pct", "")} {c.cls === "bad" ? "worsened" : c.cls === "up" ? "improved" : c.pct > 0 ? "rose" : "fell"}</span>
                    <span className="sub">{fmt(c.previous.value, unitOf(defs.data, c.key))} → {fmt(c.latest.value, unitOf(defs.data, c.key))} · {relative(c.latest.computed_at)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3"><Link to="/report" className="link">View the weekly report →</Link></div>
        </div>
      </div>
      {open.isError && <Empty title="Actions could not be loaded" body={String(open.error)} />}
    </Page>
  );
}
